import { API_BASE_URL } from '@/utils/getApiBase';
import { request } from '../request';
import { supportApiPaths } from './support-paths';
import {
  buildCustomerAdminActivityParams,
  buildCustomerAdminAffiliateParams,
  buildCustomerAdminPaymentParams,
  buildCustomerAdminReportCreateData,
  buildCustomerAdminReportParams,
  buildSupportExportParams,
  buildSupportReplyRequestData,
  buildSupportTicketQueryParams,
  normalizeSupportSearchQuery
} from './support-payloads';

export { supportApiPaths } from './support-paths';
export {
  buildCustomerAdminActivityParams,
  buildCustomerAdminAffiliateParams,
  buildCustomerAdminPaymentParams,
  buildCustomerAdminReportCreateData,
  buildCustomerAdminReportParams,
  buildSupportAttachmentPayload,
  buildSupportExportParams,
  buildSupportTicketQueryParams,
  normalizeSupportCollaborationFilter,
  normalizeSupportCustomerAccountFilter,
  normalizeSupportPriorityApiTier,
  normalizeSupportSearchQuery
} from './support-payloads';

const supportApiUrl = (path: string) => `${API_BASE_URL}${path}`;

const getRuntimeHostname = () => (typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '');

const allowedSupportUploadHostnames = () => {
  const hosts = new Set(['s3.ir-thr-at1.arvanstorage.ir', 'chat.local', 'localhost', '127.0.0.1', '[::1]']);

  try {
    hosts.add(new URL(API_BASE_URL).hostname.toLowerCase());
  } catch {
    // Ignore malformed build-time config; unsafe URLs still fail below.
  }

  const runtimeHostname = getRuntimeHostname();
  if (runtimeHostname) {
    hosts.add(runtimeHostname);
  }

  return hosts;
};

const assertSafeSupportUploadUrl = (uploadUrl: string) => {
  const parsedUrl = new URL(uploadUrl);
  const uploadHost = parsedUrl.hostname.toLowerCase();
  const allowedUploadHosts = allowedSupportUploadHostnames();
  const isLocalUploadHost = uploadHost === 'localhost' || uploadHost === '127.0.0.1' || uploadHost === '[::1]';
  const isAllowedProtocol = parsedUrl.protocol === 'https:' || (isLocalUploadHost && parsedUrl.protocol === 'http:');

  if (!isAllowedProtocol || parsedUrl.username || parsedUrl.password || !allowedUploadHosts.has(uploadHost)) {
    throw new Error('Unsafe support upload URL');
  }

  return parsedUrl.toString();
};

const normalizeSupportAttachmentDownloadUrl = (downloadPath: string, fallbackPath: string): string => {
  try {
    const apiBaseUrl = new URL(API_BASE_URL);
    const url = new URL(downloadPath, API_BASE_URL);

    if (url.origin !== apiBaseUrl.origin || url.pathname !== fallbackPath) {
      return supportApiUrl(fallbackPath);
    }

    return supportApiUrl(fallbackPath);
  } catch {
    return supportApiUrl(fallbackPath);
  }
};

/**
 * Fetch user support profile with roles
 */
export function fetchSupportUserProfile() {
  return request<Api.Support.User>({
    url: supportApiUrl(supportApiPaths.userProfile()),
    method: 'get'
  });
}

/**
 * Fetch tickets with optional filters and pagination
 * @param params Query parameters for filtering and pagination
 */
export function fetchTickets(params?: Api.Support.TicketQueryParams) {
  return request<Api.Support.PaginatedResponse<Api.Support.Ticket>>({
    url: supportApiUrl(supportApiPaths.tickets()),
    method: 'get',
    params: buildSupportTicketQueryParams(params),
    metadata: {
      operationType: 'read',
      isUserInitiated: false,
      operationName: 'fetchTickets',
      silentFailure: true
    }
  } as any);
}

/**
 * Create a new support ticket
 * @param data Ticket creation data
 */
export function createTicket(data: Api.Support.CreateTicketData) {
  return request<Api.Support.Ticket>({
    url: supportApiUrl(supportApiPaths.tickets()),
    method: 'post',
    data,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'createTicket',
      showUserErrors: true
    }
  } as any);
}

/**
 * Fetch ticket details by ticket number
 * @param ticketNumber Ticket number (string identifier)
 */
export function fetchTicketDetails(ticketNumber: string) {
  return request<Api.Support.Ticket>({
    url: supportApiUrl(supportApiPaths.ticket(ticketNumber)),
    method: 'get',
    metadata: {
      operationType: 'read',
      isUserInitiated: false,
      operationName: 'fetchTicketDetails',
      silentFailure: true
    }
  } as any);
}

/**
 * Update ticket details
 * @param ticketNumber Ticket number (string identifier)
 * @param data Updated ticket data
 */
export function updateTicket(ticketNumber: string, data: Partial<Api.Support.Ticket>) {
  return request<Api.Support.Ticket>({
    url: supportApiUrl(supportApiPaths.ticket(ticketNumber)),
    method: 'patch',
    data,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'updateTicket',
      showUserErrors: true
    }
  } as any);
}

/**
 * Assign ticket to an agent
 * @param ticketNumber Ticket number (string identifier)
 * @param agentId Agent user UUID, or null to unassign
 */
export function assignTicket(ticketNumber: string, agentId: string | null) {
  return request<Api.Support.Ticket>({
    url: supportApiUrl(supportApiPaths.ticketAssign(ticketNumber)),
    method: 'post',
    data: { agent_id: agentId },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'assignTicket',
      showUserErrors: true
    }
  } as any);
}

/**
 * Change ticket status
 * @param ticketNumber Ticket number (string identifier)
 * @param status New status
 */
export function changeTicketStatus(ticketNumber: string, status: string) {
  return request<Api.Support.Ticket>({
    url: supportApiUrl(supportApiPaths.ticketChangeStatus(ticketNumber)),
    method: 'post',
    data: { status },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'changeTicketStatus',
      showUserErrors: true
    }
  } as any);
}

/**
 * Change ticket priority
 * @param ticketNumber Ticket number (string identifier)
 * @param priority New priority
 */
export function changeTicketPriority(ticketNumber: string, priority: string) {
  return request<Api.Support.TicketActionResponse>({
    url: supportApiUrl(supportApiPaths.ticketChangePriority(ticketNumber)),
    method: 'post',
    data: { priority },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'changeTicketPriority',
      showUserErrors: true
    }
  } as any);
}

/**
 * Fetch ticket replies
 * @param ticketNumber Ticket number (string identifier)
 */
export function fetchTicketReplies(ticketNumber: string) {
  return request<Api.Support.TicketReply[]>({
    url: supportApiUrl(supportApiPaths.ticketReplies(ticketNumber)),
    method: 'get'
  });
}

/**
 * Add reply to ticket
 * @param ticketNumber Ticket number (string identifier)
 * @param reply Reply data
 */
export function addTicketReply(ticketNumber: string, reply: Api.Support.CreateReplyData) {
  const { data, idempotencyKey } = buildSupportReplyRequestData(reply);

  return request<Api.Support.TicketReply>({
    url: supportApiUrl(supportApiPaths.ticketReplies(ticketNumber)),
    method: 'post',
    data,
    ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'addTicketReply',
      showUserErrors: true
    }
  } as any);
}

export function createCollaborationInternalNote(ticketNumber: string, data: Api.Support.CreateInternalNoteData) {
  return request<Api.Support.TicketCollaborationMutationResponse>({
    url: supportApiUrl(supportApiPaths.ticketCollaborationInternalNote(ticketNumber)),
    method: 'post',
    data,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'createCollaborationInternalNote',
      showUserErrors: true
    }
  } as any);
}

export function updateTicketWatchers(ticketNumber: string, data: Api.Support.TicketWatcherUpdateData) {
  return request<Api.Support.TicketCollaborationMutationResponse>({
    url: supportApiUrl(supportApiPaths.ticketCollaborationWatchers(ticketNumber)),
    method: 'put',
    data,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'updateTicketWatchers',
      showUserErrors: true
    }
  } as any);
}

export function acknowledgeTicketAttentionRequest(ticketNumber: string, requestId: string | number) {
  return request<Api.Support.TicketCollaborationMutationResponse>({
    url: supportApiUrl(supportApiPaths.ticketCollaborationAcknowledge(ticketNumber, requestId)),
    method: 'post',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'acknowledgeTicketAttentionRequest',
      showUserErrors: true
    }
  } as any);
}

export function resolveTicketAttentionRequest(ticketNumber: string, requestId: string | number) {
  return request<Api.Support.TicketCollaborationMutationResponse>({
    url: supportApiUrl(supportApiPaths.ticketCollaborationResolve(ticketNumber, requestId)),
    method: 'post',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'resolveTicketAttentionRequest',
      showUserErrors: true
    }
  } as any);
}

export function handoffTicket(ticketNumber: string, data: { to_agent_id: string; note?: string }) {
  return request<Api.Support.TicketCollaborationMutationResponse>({
    url: supportApiUrl(supportApiPaths.ticketCollaborationHandoff(ticketNumber)),
    method: 'post',
    data,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'handoffTicket',
      showUserErrors: true
    }
  } as any);
}

/**
 * Fetch ticket categories
 */
export function fetchTicketCategories() {
  return request<Api.Support.TicketCategory[]>({
    url: supportApiUrl(supportApiPaths.categories()),
    method: 'get'
  });
}

export function fetchUserAddresses() {
  return request<Api.Support.AddressListResponse>({
    url: supportApiUrl(supportApiPaths.userAddresses()),
    method: 'get'
  });
}

export function createUserAddress(data: Api.Support.AddressCreatePayload) {
  return request<Api.Support.AddressCreateResponse>({
    url: supportApiUrl(supportApiPaths.userAddresses()),
    method: 'post',
    data,
    meta: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'createUserAddress',
      showUserErrors: true
    }
  } as any);
}

export function updateUserAddress(addressId: number, data: Partial<Api.Support.AddressCreatePayload>) {
  return request<Api.Support.AddressCreateResponse>({
    url: supportApiUrl(supportApiPaths.userAddress(addressId)),
    method: 'patch',
    data,
    meta: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'updateUserAddress',
      showUserErrors: true
    }
  } as any);
}

export function deleteUserAddress(addressId: number) {
  return request<Api.Support.AddressDeleteResponse>({
    url: supportApiUrl(supportApiPaths.userAddress(addressId)),
    method: 'delete',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'deleteUserAddress',
      showUserErrors: true
    }
  } as any);
}

export function deleteAllUserAddresses() {
  return request<Api.Support.AddressArchiveAllResponse>({
    url: supportApiUrl(supportApiPaths.userAddresses()),
    method: 'delete',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'deleteAllUserAddresses',
      showUserErrors: true
    }
  } as any);
}

export function fetchCategoryFields(categoryId: number, language?: Api.Support.KnowledgeBaseLanguage) {
  return request<Api.Support.CategoryFieldRequirements>({
    url: supportApiUrl(supportApiPaths.categoryFields(categoryId)),
    method: 'get',
    params: { language }
  });
}

export function validateCategoryFields(
  categoryId: number,
  fields: Record<string, string | number | string[] | undefined>,
  language?: Api.Support.KnowledgeBaseLanguage
) {
  return request<Api.Support.FieldValidationResult>({
    url: supportApiUrl(supportApiPaths.validateCategoryFields(categoryId)),
    method: 'post',
    data: { fields, language }
  });
}

/**
 * Fetch knowledge base categories
 * @param params Query parameters including language
 */
export function fetchKnowledgeBaseCategories(params?: { language?: Api.Support.KnowledgeBaseLanguage }) {
  return request<Api.Support.KnowledgeBaseCategory[]>({
    url: supportApiUrl(supportApiPaths.knowledgeBaseCategories()),
    method: 'get',
    params
  });
}

/**
 * Fetch knowledge base articles
 * @param params Query parameters for filtering including language
 */
export function fetchKnowledgeBaseArticles(params?: {
  category?: number;
  language?: Api.Support.KnowledgeBaseLanguage;
  ordering?: string;
  limit?: number;
}) {
  return request<Api.Support.KnowledgeBaseArticle[]>({
    url: supportApiUrl(supportApiPaths.knowledgeBaseArticles()),
    method: 'get',
    params
  });
}

/**
 * Fetch a single knowledge base article by ID
 * @param id Article ID
 * @param params Query parameters including language
 */
export function fetchKnowledgeBaseArticle(id: number, params?: { language?: Api.Support.KnowledgeBaseLanguage }) {
  return request<Api.Support.KnowledgeBaseArticle>({
    url: supportApiUrl(supportApiPaths.knowledgeBaseArticle(id)),
    method: 'get',
    params
  });
}

/**
 * Fetch popular knowledge base articles (sorted by view count)
 * @param params Query parameters including language and limit
 */
export function fetchPopularArticles(params?: { language?: Api.Support.KnowledgeBaseLanguage; limit?: number }) {
  return request<Api.Support.KnowledgeBaseArticle[]>({
    url: supportApiUrl(supportApiPaths.knowledgeBasePopular()),
    method: 'get',
    params
  });
}

/**
 * Search knowledge base articles
 * @param query Search query
 * @param language Language code (e.g., 'fa-IR', 'en-US')
 */
export function searchKnowledgeBaseArticles(query: string, language?: Api.Support.KnowledgeBaseLanguage) {
  return request<{
    results: Api.Support.KnowledgeBaseArticle[];
    documentation_results: Api.Support.KnowledgeBaseDocumentationResult[];
    query: string;
    language: Api.Support.KnowledgeBaseLanguage;
    count: number;
    documentation_count: number;
    total_count: number;
  }>({
    url: supportApiUrl(supportApiPaths.knowledgeBaseSearch()),
    method: 'get',
    params: { q: normalizeSupportSearchQuery(query), language }
  });
}

/**
 * Mark knowledge base article as helpful
 * @param id Article ID
 */
export function markArticleHelpful(id: number) {
  return request<Api.Support.KnowledgeBaseArticle>({
    url: supportApiUrl(supportApiPaths.markArticleHelpful(id)),
    method: 'post'
  });
}

/**
 * Mark knowledge base article as not helpful
 * @param id Article ID
 */
export function markArticleNotHelpful(id: number) {
  return request<Api.Support.KnowledgeBaseArticle>({
    url: supportApiUrl(supportApiPaths.markArticleNotHelpful(id)),
    method: 'post'
  });
}

/**
 * Get file upload information (allowed types and limits)
 */
export function fetchFileUploadInfo() {
  return request<Api.Support.FileUploadInfo>({
    url: supportApiUrl(supportApiPaths.fileUpload()),
    method: 'get'
  });
}

/**
 * Upload support file and get presigned URL
 * @param data File upload data
 */
export function uploadSupportFile(data: {
  file_name: string;
  file_type: string;
  file_size: number;
  file_checksum?: string;
  ticket_number?: string;
}) {
  return request<Api.Support.FileUploadResponse>({
    url: supportApiUrl(supportApiPaths.fileUpload()),
    method: 'post',
    data
  });
}

export function deleteSupportUploadedFile(fileId: string) {
  return request<void>({
    url: supportApiUrl(supportApiPaths.fileUploadItem(fileId)),
    method: 'delete',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'deleteSupportUploadedFile',
      showUserErrors: true
    }
  } as any);
}

export function getSupportAttachmentDownloadUrl(ticketNumber: string, attachment: Api.Support.TicketAttachment) {
  const fallbackPath = supportApiPaths.ticketAttachmentDownload(ticketNumber, attachment.id);
  return normalizeSupportAttachmentDownloadUrl(attachment.download_url || fallbackPath, fallbackPath);
}

/**
 * Preview safety primitives live in a dependency-free module so they can be
 * unit-tested in isolation and shared with the preview composable/component as
 * the single source of truth for what may be rendered inline.
 */
export {
  isSupportAttachmentContentTypeAllowed,
  normalizeContentType,
  SUPPORT_ATTACHMENT_PREVIEW_EXTENSIONS,
  SUPPORT_ATTACHMENT_PREVIEW_MIME,
  SUPPORT_ATTACHMENT_ACTIVE_MIME_TYPES
} from '@/utils/support-attachment-preview';
export type { SupportAttachmentPreviewKind } from '@/utils/support-attachment-preview';

import { normalizeContentType } from '@/utils/support-attachment-preview';

export interface SupportAttachmentBlobResult {
  blob: Blob | null;
  contentType: string;
  error: unknown;
  statusCode: number | null;
}

/**
 * Fetch an attachment through the authenticated request layer as a Blob.
 *
 * This is the safety-critical replacement for pointing a browser-native
 * <a href> / <img src> / <iframe src> at the raw authenticated endpoint:
 * the request carries the CSRF token, errors are surfaced to the caller,
 * and the caller controls how the bytes are rendered/saved.
 */
export async function fetchSupportAttachmentBlob(
  ticketNumber: string,
  attachment: Api.Support.TicketAttachment
): Promise<SupportAttachmentBlobResult> {
  const downloadPath = supportApiPaths.ticketAttachmentDownload(ticketNumber, attachment.id);

  const { data, error, statusCode } = await request<Blob, 'blob'>({
    url: supportApiUrl(downloadPath),
    method: 'get',
    responseType: 'blob',
    metadata: {
      operationType: 'read',
      isUserInitiated: true,
      operationName: 'fetchSupportAttachmentBlob',
      showUserErrors: true
    }
  } as any);

  const blob = data instanceof Blob ? data : null;
  const contentType = normalizeContentType(blob?.type || attachment.content_type || '');

  return { blob, contentType, error, statusCode: statusCode ?? null };
}

export function retrySupportAttachmentScan(ticketNumber: string, attachmentId: number, waitSeconds = 60) {
  return request<Api.Support.AttachmentScanRetryResponse>({
    url: supportApiUrl(supportApiPaths.ticketAttachmentRetryScan(ticketNumber, attachmentId)),
    method: 'post',
    params: waitSeconds > 0 ? { wait: waitSeconds } : undefined,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'retrySupportAttachmentScan',
      showUserErrors: true
    }
  } as any);
}

export function getSupportAttachmentScanStatus(ticketNumber: string, attachmentId: number) {
  return request<Api.Support.AttachmentScanRetryResponse>({
    url: supportApiUrl(supportApiPaths.ticketAttachmentScanStatus(ticketNumber, attachmentId)),
    method: 'get',
    metadata: {
      operationType: 'read',
      isUserInitiated: false,
      operationName: 'getSupportAttachmentScanStatus',
      silentFailure: true
    }
  } as any);
}

const calculateSupportFileChecksum = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
};

export async function uploadSelectedSupportFiles(files: File[], ticketNumber?: string): Promise<string[]> {
  if (!files.length) return [];

  const uploadedFileIds: string[] = [];

  for (const file of files) {
    const { data, error } = await uploadSupportFile({
      file_name: file.name,
      file_type: file.type || 'application/octet-stream',
      file_size: file.size,
      file_checksum: await calculateSupportFileChecksum(file),
      ticket_number: ticketNumber
    });

    if (error || !data?.upload_url) {
      throw error || new Error('Unable to initialize support file upload');
    }

    const safeUploadUrl = assertSafeSupportUploadUrl(data.upload_url);

    if (data.upload_fields && Object.keys(data.upload_fields).length > 0) {
      const formData = new FormData();
      Object.entries(data.upload_fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(safeUploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Unable to upload support file');
      }
    } else {
      const uploadResponse = await fetch(safeUploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Unable to upload support file');
      }
    }

    uploadedFileIds.push(String(data.file_id));
  }

  return uploadedFileIds;
}

/**
 * Fetch support agents list (admin/agent only)
 */
export function fetchSupportAgents() {
  return request<Api.Support.User[]>({
    url: supportApiUrl(supportApiPaths.supportAgents()),
    method: 'get'
  });
}

/**
 * Fetch support admins list (admin only)
 */
export function fetchSupportAdmins() {
  return request<Api.Support.User[]>({
    url: supportApiUrl(supportApiPaths.supportAdmins()),
    method: 'get'
  });
}

/**
 * Create support role (admin only)
 * @param data Role creation data
 */
export function createSupportRole(data: { user_id: string; role: 'agent' | 'admin' }) {
  return request<Api.Support.SupportRole>({
    url: supportApiUrl(supportApiPaths.roles()),
    method: 'post',
    data
  });
}

/**
 * Fetch canned responses (agent/admin only)
 */
export function fetchCannedResponses() {
  return request<Api.Support.CannedResponse[]>({
    url: supportApiUrl(supportApiPaths.cannedResponses()),
    method: 'get'
  });
}

/**
 * Use canned response (agent/admin only)
 * @param id Canned response ID
 */
export function useCannedResponse(id: number) {
  return request<Api.Support.CannedResponse>({
    url: supportApiUrl(supportApiPaths.useCannedResponse(id)),
    method: 'post'
  });
}

/**
 * Fetch agent reply templates (agent/admin only).
 * Returns the full locale map (fa-IR / en-US) with base scaffold parts
 * and lifecycle scenario bodies so agents can switch language client-side.
 */
export function fetchReplyTemplates() {
  return request<Api.Support.ReplyTemplatesResponse>({
    url: supportApiUrl(supportApiPaths.replyTemplates()),
    method: 'get'
  });
}

/**
 * Fetch ticket templates (agent/admin only)
 */
export function fetchTicketTemplates() {
  return request<Api.Support.TicketTemplate[]>({
    url: supportApiUrl(supportApiPaths.ticketTemplates()),
    method: 'get'
  });
}

/**
 * Fetch support analytics (admin only)
 */
export function fetchSupportAnalytics() {
  return request<Api.Support.SupportAnalytics>({
    url: supportApiUrl(supportApiPaths.analytics()),
    method: 'get'
  });
}

/**
 * Fetch dashboard statistics for current user
 */
export function fetchDashboardStats() {
  return request<Api.Support.DashboardStats>({
    url: supportApiUrl(supportApiPaths.dashboardStats()),
    method: 'get',
    metadata: {
      operationType: 'read',
      isUserInitiated: false,
      operationName: 'fetchDashboardStats',
      silentFailure: true
    }
  } as any);
}

/**
 * Search tickets with query
 * @param query Search query
 * @param filters Additional filters
 */
export function searchTickets(query: string, filters?: Api.Support.TicketFilters) {
  return fetchTickets({
    ...filters,
    search: query
  });
}

/**
 * Close ticket
 * @param ticketNumber Ticket number (string identifier)
 */
export function closeTicket(ticketNumber: string) {
  return request<Api.Support.TicketActionResponse>({
    url: supportApiUrl(supportApiPaths.ticketClose(ticketNumber)),
    method: 'post',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'closeTicket',
      showUserErrors: true
    }
  } as any);
}

/**
 * Reopen ticket
 * @param ticketNumber Ticket number (string identifier)
 */
export function reopenTicket(ticketNumber: string) {
  return request<Api.Support.TicketActionResponse>({
    url: supportApiUrl(supportApiPaths.ticketReopen(ticketNumber)),
    method: 'post',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'reopenTicket',
      showUserErrors: true
    }
  } as any);
}

/**
 * Mark a ticket as viewed by the current support agent (agent-only).
 *
 * SECURITY: Backend rejects non-agent callers; the recorded "seen" data is
 * only ever exposed to other agents via `agent_read_state`.
 * @param ticketNumber Ticket number (string identifier)
 */
export function markTicketViewed(ticketNumber: string) {
  return request<Api.Support.MarkViewedResponse>({
    url: supportApiUrl(supportApiPaths.ticketMarkViewed(ticketNumber)),
    method: 'post',
    metadata: {
      operationType: 'write',
      isUserInitiated: false,
      operationName: 'markTicketViewed',
      silentFailure: true
    }
  } as any);
}

/**
 * Fetch ticket activity/timeline
 * @param ticketNumber Ticket number (string identifier)
 * @param params Optional parameters for filtering and pagination
 */
export function fetchTicketActivity(
  ticketNumber: string,
  params?: {
    include_internal?: boolean;
    limit?: number;
    offset?: number;
  }
) {
  return request<Api.Support.PaginatedActivityResponse>({
    url: supportApiUrl(supportApiPaths.ticketActivity(ticketNumber)),
    method: 'get',
    params,
    metadata: {
      operationType: 'read',
      isUserInitiated: false,
      operationName: 'fetchTicketActivity',
      silentFailure: true
    }
  } as any);
}

// ==================== CUSTOMER ADMINISTRATION ====================

interface CustomerAdminRequestOptions {
  signal?: AbortSignal;
}

const customerAdminReadMetadata = (operationName: string) => ({
  operationType: 'read' as const,
  isUserInitiated: false,
  operationName,
  silentFailure: true
});

// ---- Target-aware customer administration -------------------------------
// A customer-admin surface can be reached either from a ticket (customer
// resolved from Ticket.created_by) or directly by user id (agent/admin
// customer search). These resolvers keep the API layer agnostic to the scope
// while pointing each section at the correct ticket- or user-scoped endpoint.

type CustomerAdminSection =
  | 'overview'
  | 'tickets'
  | 'credits'
  | 'apiKeys'
  | 'apiActivity'
  | 'payments'
  | 'affiliates'
  | 'subscription'
  | 'gatewayBypass'
  | 'subscriptionCancel';

type CustomerAdminSectionBuilder = (identifier: string) => string;

const ticketCustomerAdminSectionBuilders: Record<CustomerAdminSection, CustomerAdminSectionBuilder> = {
  overview: supportApiPaths.ticketCustomerAdminOverview,
  tickets: supportApiPaths.ticketCustomerAdminTickets,
  credits: supportApiPaths.ticketCustomerAdminCredits,
  apiKeys: supportApiPaths.ticketCustomerAdminApiKeys,
  apiActivity: supportApiPaths.ticketCustomerAdminApiActivity,
  payments: supportApiPaths.ticketCustomerAdminPayments,
  affiliates: supportApiPaths.ticketCustomerAdminAffiliates,
  subscription: supportApiPaths.ticketCustomerAdminSubscription,
  gatewayBypass: supportApiPaths.ticketCustomerAdminGatewayBypass,
  subscriptionCancel: supportApiPaths.ticketCustomerAdminSubscriptionCancel
};

const userCustomerAdminSectionBuilders: Record<CustomerAdminSection, CustomerAdminSectionBuilder> = {
  overview: supportApiPaths.userCustomerAdminOverview,
  tickets: supportApiPaths.userCustomerAdminTickets,
  credits: supportApiPaths.userCustomerAdminCredits,
  apiKeys: supportApiPaths.userCustomerAdminApiKeys,
  apiActivity: supportApiPaths.userCustomerAdminApiActivity,
  payments: supportApiPaths.userCustomerAdminPayments,
  affiliates: supportApiPaths.userCustomerAdminAffiliates,
  subscription: supportApiPaths.userCustomerAdminSubscription,
  gatewayBypass: supportApiPaths.userCustomerAdminGatewayBypass,
  subscriptionCancel: supportApiPaths.userCustomerAdminSubscriptionCancel
};

function customerAdminSectionPath(target: Api.Support.CustomerAdminTarget, section: CustomerAdminSection): string {
  if (target.kind === 'ticket') {
    const build = ticketCustomerAdminSectionBuilders[section] ?? ticketCustomerAdminSectionBuilders.overview;
    return build(target.ticketNumber);
  }
  const build = userCustomerAdminSectionBuilders[section] ?? userCustomerAdminSectionBuilders.overview;
  return build(target.userId);
}

function customerAdminPaymentRecheckPath(target: Api.Support.CustomerAdminTarget, paymentId: number): string {
  return target.kind === 'ticket'
    ? supportApiPaths.ticketCustomerAdminPaymentRecheck(target.ticketNumber, paymentId)
    : supportApiPaths.userCustomerAdminPaymentRecheck(target.userId, paymentId);
}

function customerAdminCreditPackageExtendPath(target: Api.Support.CustomerAdminTarget, packageId: number): string {
  return target.kind === 'ticket'
    ? supportApiPaths.ticketCustomerAdminCreditPackageExtend(target.ticketNumber, packageId)
    : supportApiPaths.userCustomerAdminCreditPackageExtend(target.userId, packageId);
}

function customerAdminGrantExtendPath(target: Api.Support.CustomerAdminTarget, grantId: number): string {
  return target.kind === 'ticket'
    ? supportApiPaths.ticketCustomerAdminGrantExtend(target.ticketNumber, grantId)
    : supportApiPaths.userCustomerAdminGrantExtend(target.userId, grantId);
}

export interface CustomerAdminSearchOptions extends CustomerAdminRequestOptions {
  /**
   * Opt into the broad `contains` scan. The default fast path matches a phone
   * or email exactly and a username by prefix, so it stays index-bound. Deep
   * search is the only way to surface soft-deleted accounts (whose identifiers
   * carry a `_deleted_<n>` suffix) and is materially slower.
   */
  deep?: boolean;
}

/** Search the customer directory by username, email, or phone (agent/admin only). */
export function searchCustomerAdminCustomers(query: string, options: CustomerAdminSearchOptions = {}) {
  return request<Api.Support.CustomerAdminSearchResponse>({
    url: supportApiUrl(supportApiPaths.customerAdminSearch()),
    method: 'get',
    params: options.deep ? { q: query, deep: true } : { q: query },
    signal: options.signal,
    metadata: {
      operationType: 'read',
      isUserInitiated: true,
      operationName: 'searchCustomerAdminCustomers',
      showUserErrors: true
    }
  } as any);
}

/** Fetch a customer's recent tickets, stages, and duplicate-detection groups. */
export function fetchCustomerAdminTickets(
  target: Api.Support.CustomerAdminTarget,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminTicketsResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'tickets')),
    method: 'get',
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminTickets')
  } as any);
}

export function fetchCustomerAdminOverview(
  target: Api.Support.CustomerAdminTarget,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminOverviewResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'overview')),
    method: 'get',
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminOverview')
  } as any);
}

export function fetchCustomerAdminCredits(
  target: Api.Support.CustomerAdminTarget,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminCreditsResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'credits')),
    method: 'get',
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminCredits')
  } as any);
}

export function fetchCustomerAdminApiKeys(
  target: Api.Support.CustomerAdminTarget,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminApiKeysResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'apiKeys')),
    method: 'get',
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminApiKeys')
  } as any);
}

export function fetchCustomerAdminApiActivity(
  target: Api.Support.CustomerAdminTarget,
  window: Api.Support.CustomerAdminActivityWindow = '24h',
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminApiActivityResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'apiActivity')),
    method: 'get',
    params: buildCustomerAdminActivityParams(window),
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminApiActivity')
  } as any);
}

export function fetchCustomerAdminPayments(
  target: Api.Support.CustomerAdminTarget,
  filters?: Api.Support.CustomerAdminPaymentFilters,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminPaymentsResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'payments')),
    method: 'get',
    params: buildCustomerAdminPaymentParams(filters),
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminPayments')
  } as any);
}

export function recheckCustomerAdminPayment(
  target: Api.Support.CustomerAdminTarget,
  paymentId: number,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminPaymentRecheckResponse>({
    url: supportApiUrl(customerAdminPaymentRecheckPath(target, paymentId)),
    method: 'post',
    data: {},
    signal: options.signal,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'recheckCustomerAdminPayment',
      showUserErrors: true
    }
  } as any);
}

export function updateCustomerAdminGatewayBypass(
  target: Api.Support.CustomerAdminTarget,
  enabled: boolean,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminGatewayBypassResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'gatewayBypass')),
    method: 'post',
    data: { enabled } satisfies Api.Support.CustomerAdminGatewayBypassData,
    signal: options.signal,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'updateCustomerAdminGatewayBypass',
      showUserErrors: true
    }
  } as any);
}

export function fetchCustomerAdminSubscription(
  target: Api.Support.CustomerAdminTarget,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminSubscriptionResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'subscription')),
    method: 'get',
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminSubscription')
  } as any);
}

export function cancelCustomerAdminSubscription(
  target: Api.Support.CustomerAdminTarget,
  trackingCode: string,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminSubscriptionCancelResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'subscriptionCancel')),
    method: 'post',
    data: { tracking_code: trackingCode } satisfies Api.Support.CustomerAdminSubscriptionCancelData,
    signal: options.signal,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'cancelCustomerAdminSubscription',
      showUserErrors: true
    }
  } as any);
}

export function extendCustomerAdminCreditPackage(
  target: Api.Support.CustomerAdminTarget,
  packageId: number,
  params: Api.Support.CustomerAdminCreditExtendData & CustomerAdminRequestOptions
) {
  const { signal, ...data } = params;
  return request<Api.Support.CustomerAdminCreditPackageExtendResponse>({
    url: supportApiUrl(customerAdminCreditPackageExtendPath(target, packageId)),
    method: 'post',
    data,
    signal,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'extendCustomerAdminCreditPackage',
      showUserErrors: true
    }
  } as any);
}

export function extendCustomerAdminGrant(
  target: Api.Support.CustomerAdminTarget,
  grantId: number,
  params: Api.Support.CustomerAdminGrantExtendData & CustomerAdminRequestOptions
) {
  const { signal, ...data } = params;
  return request<Api.Support.CustomerAdminGrantExtendResponse>({
    url: supportApiUrl(customerAdminGrantExtendPath(target, grantId)),
    method: 'post',
    data,
    signal,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'extendCustomerAdminGrant',
      showUserErrors: true
    }
  } as any);
}

export function fetchCustomerAdminAffiliates(
  target: Api.Support.CustomerAdminTarget,
  filters?: Api.Support.CustomerAdminAffiliateFilters,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminAffiliatesResponse>({
    url: supportApiUrl(customerAdminSectionPath(target, 'affiliates')),
    method: 'get',
    params: buildCustomerAdminAffiliateParams(filters),
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminAffiliates')
  } as any);
}

export function fetchCustomerAdminReports(
  ticketNumber: string,
  filters?: Api.Support.CustomerAdminReportFilters,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminReportsResponse>({
    url: supportApiUrl(supportApiPaths.ticketCustomerAdminReports(ticketNumber)),
    method: 'get',
    params: buildCustomerAdminReportParams(filters),
    signal: options.signal,
    metadata: customerAdminReadMetadata('fetchCustomerAdminReports')
  } as any);
}

export function createCustomerAdminReport(
  ticketNumber: string,
  data: Api.Support.CustomerAdminReportCreateData,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminReportCreateResponse>({
    url: supportApiUrl(supportApiPaths.ticketCustomerAdminReports(ticketNumber)),
    method: 'post',
    data: buildCustomerAdminReportCreateData(data),
    signal: options.signal,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'createCustomerAdminReport',
      showUserErrors: true
    }
  } as any);
}

/** Build a customer-facing app path without trusting a storage URL or arbitrary server path. */
export function getCustomerAdminReportCustomerPath(fileId: string): string {
  return `/platform/usage/reports?report=${encodeURIComponent(fileId)}`;
}

/** Accept only HTTPS URLs issued by one of the configured report storage providers. */
export function normalizeCustomerAdminReportDownloadUrl(downloadUrl: string): string | null {
  try {
    const url = new URL(downloadUrl);
    const hostname = url.hostname.toLowerCase();
    const isTrustedStorageHost =
      hostname === 'arvanstorage.ir' ||
      hostname.endsWith('.arvanstorage.ir') ||
      hostname === 'r2.cloudflarestorage.com' ||
      hostname.endsWith('.r2.cloudflarestorage.com');

    if (url.protocol !== 'https:' || url.username || url.password || !isTrustedStorageHost) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/** Request a short-lived storage URL after the backend enforces report ownership and availability. */
export function fetchCustomerAdminReportDownloadUrl(
  ticketNumber: string,
  fileId: string,
  options: CustomerAdminRequestOptions = {}
) {
  return request<Api.Support.CustomerAdminReportDownloadResponse>({
    url: supportApiUrl(supportApiPaths.ticketCustomerAdminReportDownload(ticketNumber, fileId)),
    method: 'get',
    signal: options.signal,
    metadata: {
      operationType: 'read',
      isUserInitiated: true,
      operationName: 'fetchCustomerAdminReportDownloadUrl',
      showUserErrors: true
    }
  } as any);
}

// ==================== BULK ACTIONS ====================

/**
 * Bulk assign tickets to an agent
 * @param ticketNumbers Array of ticket numbers
 * @param agentId Agent user UUID (null to unassign)
 */
export function bulkAssignTickets(ticketNumbers: string[], agentId: string | null) {
  return request<Api.Support.BulkActionResponse>({
    url: supportApiUrl(supportApiPaths.bulkAssignTickets()),
    method: 'post',
    data: {
      ticket_numbers: ticketNumbers,
      agent_id: agentId
    },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'bulkAssignTickets',
      showUserErrors: true
    }
  } as any);
}

/**
 * Bulk change ticket status
 * @param ticketNumbers Array of ticket numbers
 * @param status New status
 */
export function bulkChangeStatus(ticketNumbers: string[], status: Api.Support.Ticket['status']) {
  return request<Api.Support.BulkActionResponse>({
    url: supportApiUrl(supportApiPaths.bulkChangeStatus()),
    method: 'post',
    data: {
      ticket_numbers: ticketNumbers,
      status
    },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'bulkChangeStatus',
      showUserErrors: true
    }
  } as any);
}

/**
 * Bulk change ticket priority
 * @param ticketNumbers Array of ticket numbers
 * @param priority New priority
 */
export function bulkChangePriority(ticketNumbers: string[], priority: Api.Support.Ticket['priority']) {
  return request<Api.Support.BulkActionResponse>({
    url: supportApiUrl(supportApiPaths.bulkChangePriority()),
    method: 'post',
    data: {
      ticket_numbers: ticketNumbers,
      priority
    },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'bulkChangePriority',
      showUserErrors: true
    }
  } as any);
}

// ==================== EXPORT ====================

/**
 * Export tickets to CSV or Excel
 * @param params Export parameters and filters
 * @returns Blob response for file download
 */
export function exportTickets(params?: Api.Support.ExportParams) {
  return request<Blob>({
    url: supportApiUrl(supportApiPaths.ticketExport()),
    method: 'get',
    params: buildSupportExportParams(params),
    responseType: 'blob',
    metadata: {
      operationType: 'read',
      isUserInitiated: true,
      operationName: 'exportTickets',
      showUserErrors: true
    }
  } as any);
}

// ==================== NOTIFICATION PREFERENCES ====================

/**
 * Fetch notification preferences for the current user
 */
export async function fetchNotificationPreferences() {
  const result = await request<Api.Support.NotificationPreferencesResponse>({
    url: supportApiUrl(supportApiPaths.notificationPreferences()),
    method: 'get',
    metadata: {
      operationType: 'read',
      isUserInitiated: false,
      operationName: 'fetchNotificationPreferences',
      silentFailure: true
    }
  } as any);

  // Extract preferences from response wrapper
  if (result.data?.preferences) {
    return { data: result.data.preferences, error: result.error };
  }
  return { data: null, error: result.error };
}

/**
 * Update notification preferences
 * @param data Partial notification preferences data
 */
export async function updateNotificationPreferences(data: Api.Support.UpdateNotificationPreferencesData) {
  const result = await request<Api.Support.NotificationPreferencesResponse>({
    url: supportApiUrl(supportApiPaths.updateNotificationPreferences()),
    method: 'patch',
    data,
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'updateNotificationPreferences',
      showUserErrors: true
    }
  } as any);

  // Extract preferences from response wrapper
  if (result.data?.preferences) {
    return { data: result.data.preferences, error: result.error };
  }
  return { data: null, error: result.error };
}

/**
 * Reset notification preferences to defaults
 */
export async function resetNotificationPreferences() {
  const result = await request<Api.Support.NotificationPreferencesResponse>({
    url: supportApiUrl(supportApiPaths.resetNotificationPreferences()),
    method: 'post',
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'resetNotificationPreferences',
      showUserErrors: true
    }
  } as any);

  // Extract preferences from response wrapper
  if (result.data?.preferences) {
    return { data: result.data.preferences, error: result.error };
  }
  return { data: null, error: result.error };
}

/**
 * Toggle all notifications on or off
 * @param enabled Whether to enable or disable all notifications
 */
export async function toggleAllNotifications(enabled: boolean) {
  const result = await request<Api.Support.NotificationPreferencesResponse>({
    url: supportApiUrl(supportApiPaths.toggleNotificationPreferences()),
    method: 'post',
    data: { enabled },
    metadata: {
      operationType: 'write',
      isUserInitiated: true,
      operationName: 'toggleAllNotifications',
      showUserErrors: true
    }
  } as any);

  // Extract preferences from response wrapper
  if (result.data?.preferences) {
    return { data: result.data.preferences, error: result.error };
  }
  return { data: null, error: result.error };
}
